import React, { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { useComprehensiveReports, useLandlords, useLandlordProperties } from "services/hooks/rentals";
import * as XLSX from "xlsx";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "UGX",
    minimumFractionDigits: 0,
  }).format(amount ?? 0);
};

/** Filter full report data by current filters and compute summary from filtered rows (used when reading from cache). */
function filterReportDataAndComputeSummary(fullReportData, filters, landlords = [], landlordProperties = []) {
  const rows = Array.isArray(fullReportData) ? fullReportData : [];
  const landlordName = filters.landlord_id
    ? (landlords.find((l) => l.id === Number(filters.landlord_id))?.business_name ?? "")
    : null;
  const propertyName = filters.landlord_id && filters.rental_id
    ? (landlordProperties.find((p) => p.id === Number(filters.rental_id))?.rental_name ?? landlordProperties.find((p) => p.id === Number(filters.rental_id))?.name ?? "")
    : null;
  const monthStr = (filters.month || "").toString();
  const yearStr = (filters.year || "").toString();

  const filtered = rows.filter((row) => {
    if (landlordName && (row["Landlord Name"] ?? "") !== landlordName) return false;
    if (propertyName && (row["Property Name / Description"] ?? "") !== propertyName) return false;
    const monthPaidFor = (row["Month Paid For"] ?? "").toString();
    const transactionDate = (row["Transaction Date"] ?? "").toString();
    if (monthStr && !monthPaidFor.includes(monthStr) && !transactionDate.includes(monthStr)) return false;
    if (yearStr && !monthPaidFor.includes(yearStr) && !transactionDate.includes(yearStr)) return false;
    return true;
  });

  const totalRent = filtered.reduce((s, r) => s + (Number(r["Monthly Rent Amount"]) || 0), 0);
  const totalPaid = filtered.reduce((s, r) => s + (Number(r["Amount Paid"]) || 0), 0);
  const totalOutstanding = filtered.reduce((s, r) => s + (Number(r["Outstanding Amount"]) || 0), 0);
  const distinctUnits = new Set(filtered.map((r) => `${r["Property Name / Description"] ?? ""}|${r["Unit Number"] ?? ""}`).filter(Boolean)).size;

  const summary = {
    total_units: distinctUnits,
    occupied_units: filtered.length,
    unoccupied_units: Math.max(0, distinctUnits - filtered.length),
    total_rent_amount: totalRent,
    total_paid_amount: totalPaid,
    total_outstanding: totalOutstanding,
    total_management_fees: 0,
  };

  return { reportData: filtered, summary };
}

const buildExcelAndDownload = (reportData, summary, filters, landlords, landlordProperties, formatCurrencyFn) => {
  const data = Array.isArray(reportData) ? reportData : [];
  const summ = summary ?? null;
  const landlordName = filters.landlord_id
    ? (landlords?.find((l) => l.id === Number(filters.landlord_id))?.business_name ?? `Landlord #${filters.landlord_id}`)
    : "All Landlords";
  const propertyName = filters.rental_id
    ? (landlordProperties?.find((p) => p.id === Number(filters.rental_id))?.rental_name ?? `Property #${filters.rental_id}`)
    : "All Properties";

  const rows = [
    ["Comprehensive Report"],
    [],
    ["Report parameters"],
    ["Landlord", "Property", "Month", "Year", "Include Unoccupied"],
    [landlordName, propertyName, filters.month || "All Months", filters.year || "All Years", filters.include_unpaid ? "Yes" : "No"],
    [],
    ["Summary"],
    ["Total Units", "Occupied", "Unoccupied", "Total Rent Amount", "Total Paid", "Outstanding", "Management Fees"],
  ];

  if (summ) {
    rows.push([
      summ.total_units ?? "",
      summ.occupied_units ?? "",
      summ.unoccupied_units ?? "",
      formatCurrencyFn(summ.total_rent_amount),
      formatCurrencyFn(summ.total_paid_amount),
      formatCurrencyFn(summ.total_outstanding),
      formatCurrencyFn(summ.total_management_fees),
    ]);
  } else {
    rows.push(["", "", "", "", "", "", ""]);
  }

  rows.push([]);
  rows.push([
    "Transaction ID", "Transaction Date", "Tenant Name", "Tenant Contact",
    "Property Name / Description", "Unit Number", "Landlord Name", "Monthly Rent Amount",
    "Amount Paid", "Month Paid For", "Outstanding Amount", "Payment Status", "Payment Method"
  ]);

  data.forEach((row) => {
    rows.push([
      row["Transaction ID"],
      row["Transaction Date"],
      row["Tenant Name"],
      row["Tenant Contact"],
      row["Property Name / Description"],
      row["Unit Number"],
      row["Landlord Name"],
      typeof row["Monthly Rent Amount"] === "number" ? formatCurrencyFn(row["Monthly Rent Amount"]) : (row["Monthly Rent Amount"] ?? ""),
      typeof row["Amount Paid"] === "number" ? formatCurrencyFn(row["Amount Paid"]) : (row["Amount Paid"] ?? ""),
      row["Month Paid For"],
      typeof row["Outstanding Amount"] === "number" ? formatCurrencyFn(row["Outstanding Amount"]) : (row["Outstanding Amount"] ?? ""),
      row["Payment Status"],
      row["Payment Method"],
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Comprehensive Report");
  const fileName = `comprehensive-report-${filters.landlord_id || "all"}-${filters.month || "all"}-${filters.year || "all"}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

const Reports = () => {
  const [filters, setFilters] = useState({
    landlord_id: "",
    rental_id: "",
    month: "",
    year: "",
    include_unpaid: true,
  });

  const reportsCache = useSelector((state) => state.reportsCache);
  const cacheReady = Boolean(reportsCache?.hasFetched && !reportsCache?.isLoading && !reportsCache?.error && Array.isArray(reportsCache?.reportData));
  const cacheLoading = Boolean(reportsCache?.isLoading);
  /** Use cache only when "All Landlords" is selected; when a specific landlord is chosen, we fetch from the API via the hook. */
  const useCacheForDisplay = cacheReady && !filters.landlord_id;
  const skipFetch = useCacheForDisplay || cacheLoading;
  const { reportData: hookReportData, summary: hookSummary, isLoadingReports: hookLoading, error: hookError } = useComprehensiveReports(filters, { skipFetch });
  const { landlords } = useLandlords();
  const { landlordProperties, loading: loadingProperties } = useLandlordProperties(filters.landlord_id || 0);

  const { reportData, summary, isLoadingReports, error } = useMemo(() => {
    if (useCacheForDisplay && reportsCache.reportData) {
      const { reportData: filteredData, summary: filteredSummary } = filterReportDataAndComputeSummary(
        reportsCache.reportData,
        filters,
        landlords ?? [],
        []
      );
      return {
        reportData: filteredData,
        summary: filteredSummary,
        isLoadingReports: false,
        error: Boolean(reportsCache.error),
      };
    }
    return {
      reportData: hookReportData ?? [],
      summary: hookSummary,
      isLoadingReports: hookLoading,
      error: hookError,
    };
  }, [useCacheForDisplay, reportsCache, filters, landlords, hookReportData, hookSummary, hookLoading, hookError]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "landlord_id") {
        next.rental_id = "";
      }
      return next;
    });
  }, []);

  const handleDownloadExcel = () => {
    buildExcelAndDownload(reportData, summary, filters, landlords, landlordProperties, formatCurrency);
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "Paid": return "success";
      case "Partially Paid": return "warning";
      case "Unpaid": return "error";
      default: return "default";
    }
  };

  const propertyOptions = filters.landlord_id ? (landlordProperties || []) : [];
  const canDownloadExcel = summary !== null || (reportData && reportData.length > 0);
  const showLoading = isLoadingReports || (cacheLoading && !cacheReady);

  return (
    <Box m="20px">
      <Typography variant="h4" sx={{ mb: 3 }}>
        Comprehensive Reports
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Report Filters
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose &quot;All Landlords&quot; or a specific landlord; report data updates automatically. When a landlord is selected, the Property dropdown shows only that landlord&apos;s properties. Use &quot;Download Excel&quot; to export the current view.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Landlord</InputLabel>
              <Select
                value={filters.landlord_id}
                onChange={(e) => handleFilterChange("landlord_id", e.target.value)}
                label="Landlord"
              >
                <MenuItem value="">All Landlords</MenuItem>
                {(landlords || []).map((landlord) => (
                  <MenuItem key={landlord.id} value={landlord.id}>
                    {landlord.business_name ?? landlord.rental_name ?? `Landlord #${landlord.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth disabled={!filters.landlord_id}>
              <InputLabel>Property</InputLabel>
              <Select
                value={filters.rental_id}
                onChange={(e) => handleFilterChange("rental_id", e.target.value)}
                label="Property"
              >
                <MenuItem value="">All Properties</MenuItem>
                {propertyOptions.map((rental) => (
                  <MenuItem key={rental.id} value={rental.id}>
                    {rental.rental_name ?? rental.name ?? `Property #${rental.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {filters.landlord_id && loadingProperties && (
              <Typography variant="caption" color="text.secondary">Loading properties…</Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={filters.month}
                onChange={(e) => handleFilterChange("month", e.target.value)}
                label="Month"
              >
                <MenuItem value="">All Months</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={filters.year}
                onChange={(e) => handleFilterChange("year", e.target.value)}
                label="Year"
              >
                <MenuItem value="">All Years</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.include_unpaid}
                  onChange={(e) => handleFilterChange("include_unpaid", e.target.checked)}
                />
              }
              label="Include Unoccupied"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleDownloadExcel}
              disabled={!canDownloadExcel}
              sx={{ mt: 1 }}
            >
              Download Excel
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
              Report updates when you change filters. Download Excel to export the data currently on screen.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {showLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {cacheLoading ? "Loading report data in the background…" : "Loading report…"}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading reports. Please try again.
        </Alert>
      )}

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Units</Typography>
                <Typography variant="h4">{summary.total_units}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Occupied: {summary.occupied_units} | Unoccupied: {summary.unoccupied_units}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Rent Amount</Typography>
                <Typography variant="h4">{formatCurrency(summary.total_rent_amount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Paid</Typography>
                <Typography variant="h4" color="success.main">
                  {formatCurrency(summary.total_paid_amount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Outstanding</Typography>
                <Typography variant="h4" color="error.main">
                  {formatCurrency(summary.total_outstanding)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Management Fees</Typography>
                <Typography variant="h4">{formatCurrency(summary.total_management_fees)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {reportData && reportData.length > 0 && (
        <Paper sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ p: 2 }}>
            Report Data ({reportData.length} transactions)
          </Typography>
          <Divider />
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Landlord</TableCell>
                  <TableCell>Rent Amount</TableCell>
                  <TableCell>Amount Paid</TableCell>
                  <TableCell>Month Paid For</TableCell>
                  <TableCell>Outstanding</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Method</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...reportData]
                  .sort((a, b) => {
                    const da = new Date(a["Transaction Date"] || 0);
                    const db = new Date(b["Transaction Date"] || 0);
                    if (isNaN(db.getTime())) return -1;
                    if (isNaN(da.getTime())) return 1;
                    return db - da;
                  })
                  .map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{row["Transaction ID"]}</TableCell>
                    <TableCell>{row["Transaction Date"]}</TableCell>
                    <TableCell>{row["Tenant Name"]}</TableCell>
                    <TableCell>{row["Tenant Contact"]}</TableCell>
                    <TableCell>{row["Property Name / Description"]}</TableCell>
                    <TableCell>{row["Unit Number"]}</TableCell>
                    <TableCell>{row["Landlord Name"]}</TableCell>
                    <TableCell>{formatCurrency(row["Monthly Rent Amount"])}</TableCell>
                    <TableCell>{formatCurrency(row["Amount Paid"])}</TableCell>
                    <TableCell>{row["Month Paid For"]}</TableCell>
                    <TableCell>{formatCurrency(row["Outstanding Amount"])}</TableCell>
                    <TableCell>
                      <Chip
                        label={row["Payment Status"]}
                        color={getPaymentStatusColor(row["Payment Status"])}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{row["Payment Method"]}</TableCell>
                  </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {reportData && reportData.length === 0 && !isLoadingReports && summary && (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="textSecondary">
            No transactions for the selected filters
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default Reports;
